/**
 * Public registration API client (TQ #6854). These two endpoints are
 * unauthenticated (no Keycloak/Bearer) — a prospective user has no account yet.
 * Kept separate from the main ApiService singleton (which always attaches a JWT).
 */
import type { AxiosError } from 'axios'
import { createApiClient } from '../lib/apiClient'
import type {
  RegistrationRequestBody,
  RegistrationRequestResponse,
  CompleteRegistrationRequest,
  CompleteRegistrationResponse,
  RegistrationAPIError,
} from '../types/registration'

// Same convention as services/api.ts: relative /api in prod (nginx proxy),



const client = createApiClient({
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

export const RegistrationAPI = {
  /**
   * Step 1: request a registration email. The backend mails a 6-digit code +
   * magic-link. Always returns a generic success — existing emails get a
   * "you already have an account" mail instead (non-enumerable). `website` is
   * the honeypot.
   */
  async requestEmail(
    body: RegistrationRequestBody
  ): Promise<RegistrationRequestResponse> {
    try {
      const res = await client.post<RegistrationRequestResponse>(
        '/public/registration/request',
        body
      )
      return res.data
    } catch (error) {
      const axiosError = error as AxiosError<RegistrationAPIError>
      if (axiosError.response?.status === 429) {
        throw new Error('Zu viele Anfragen. Bitte später erneut versuchen.')
      }
      if (axiosError.response?.data) {
        throw new Error(
          axiosError.response.data.error || 'Mail konnte nicht gesendet werden'
        )
      }
      throw new Error('Netzwerkfehler beim Senden der Mail')
    }
  },

  /**
   * Step 2: complete registration with the code + profile data. The token
   * proves email ownership → the account is created verified.
   */
  async completeRegistration(
    data: CompleteRegistrationRequest
  ): Promise<CompleteRegistrationResponse> {
    try {
      const res = await client.post<CompleteRegistrationResponse>(
        '/public/registration/complete',
        data
      )
      return res.data
    } catch (error) {
      const axiosError = error as AxiosError<RegistrationAPIError>
      if (axiosError.response?.data) {
        throw new Error(
          axiosError.response.data.error || 'Registrierung fehlgeschlagen'
        )
      }
      throw new Error('Netzwerkfehler bei der Registrierung')
    }
  },
}
